import { useCallback } from 'react'
import { useLang } from './useLang'

/**
 * Ссылка витрины в текущем языке: `/courses` по-английски и `/ru/courses`
 * по-русски.
 *
 * Нужна потому, что язык теперь живёт в адресе. Ссылка без префикса на русской
 * странице увела бы читателя в английскую версию — и не «когда-нибудь», а на
 * первом же переходе из меню.
 *
 * Тип возвращаемого значения — объединение двух литералов, а не `string`:
 * типизированный `<Link to>` у TanStack принимает только существующие адреса, и
 * терять эту проверку ради удобства значит менять одну ошибку на другую.
 */
/**
 * Адрес в одном из двух языков. Условный тип, а не объединение: для корня это
 * «/» или «/ru» (без хвостового слэша — такого маршрута нет), для остальных —
 * путь и он же с префиксом.
 */
type Localized<T extends string> = T extends '/' ? '/' | '/ru' : T | `/ru${T}`

export function useLocalized(): <T extends string>(path: T) => Localized<T> {
  const { lang } = useLang()
  return useCallback(
    <T extends string>(path: T): Localized<T> => {
      if (lang !== 'ru') return path as Localized<T>
      // Корень по-русски — «/ru», а не «/ru/»: адрес с хвостовым слэшем
      // маршрутом не описан, да и в выдаче он был бы второй копией главной.
      return (path === '/' ? '/ru' : `/ru${path}`) as Localized<T>
    },
    [lang],
  )
}
